// GenericRetrospective.tsx
import React, {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, Typography, Paper, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ResizableNoteNode from "./ResizableNoteNode";
import NodeToolbar from "./NodeToolbar";
import DeleteableEdge from "./DeleteableEdge";
import RetrospectiveHeader from "./RetrospectiveHeader";
import { useSocket } from "../hooks/useSocket";
import { useRetrospectiveData } from "../hooks/useRetrospectiveData";
import { useRetrospectiveHandlers } from "../hooks/useRetrospectiveHandlers";
import { useRetrospectiveSocketListeners } from "../hooks/useRetrospectiveSocket";
import { useRetrospectiveRendering } from "../hooks/useRetrospectiveRendering";
import LiveCursor from "./LiveCursor";
import DraggingNodePreview from "./DraggingNodePreview";
import { NoteNodeData, ResizableNoteNodeRef } from "../types";
import {
  retrospectiveTemplates,
  TemplateConfig,
  ColumnConfig,
} from "../components/templateConfig";

interface GenericRetrospectiveProps {
  sprintId: string;
  sprintName: string;
  reactFlow: any;
  onBack: () => void;
  templateId: string;
}

// --- Sticky Note Pad Component ---
const StickyNotePad: React.FC<{
  columnId: string;
  color: string;
  childColor: string;
  onCreateNoteFromClick: (columnId: string) => void;
  position: { x: number; y: number };
  reactFlowInstance: any;
  flowRef: React.RefObject<HTMLDivElement>;
}> = ({
  columnId,
  childColor,
  onCreateNoteFromClick,
  position,
  reactFlowInstance,
  flowRef,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // --------- Handle + icon click -----------
  const handlePlusClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onCreateNoteFromClick(columnId);
    },
    [onCreateNoteFromClick, columnId]
  );

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  // --------- Visuals for sticky pad -----------
  return (
    <>
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: "absolute",
          left: position.x,
          top: position.y,
          width: 60,
          height: 60,
          cursor: "pointer",
          zIndex: 50,
          userSelect: "none",
          pointerEvents: "all",
        }}
      >
        {/* Back layers for stacked effect */}
        <Box
          sx={{
            position: "absolute",
            width: 56,
            height: 56,
            backgroundColor: childColor,
            borderRadius: 1,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            top: 4,
            left: 2,
            opacity: 0.4,
            transform: "rotate(-1deg)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 58,
            height: 58,
            backgroundColor: childColor,
            borderRadius: 1,
            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
            top: 2,
            left: 1,
            opacity: 0.7,
            transform: "rotate(0.5deg)",
            pointerEvents: "none",
          }}
        />
        {/* Top layer - the one that appears to be "pulled out" */}
        <Box
          sx={{
            position: "absolute",
            width: 60,
            height: 60,
            backgroundColor: childColor,
            borderRadius: 1,
            boxShadow: isHovered
              ? "0 6px 16px rgba(0,0,0,0.3)"
              : "0 2px 6px rgba(0,0,0,0.2)",
            top: 0,
            left: 0,
            border: "1px solid rgba(255,255,255,0.3)",
            background: `linear-gradient(135deg, ${childColor} 0%, ${childColor}ee 100%)`,
            transition: "all 0.3s ease",
            transform: isHovered
              ? "translateY(-8px) translateX(6px) rotate(5deg)"
              : "rotate(0deg)",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              top: -2,
              right: -2,
              width: 8,
              height: 8,
              backgroundColor: "rgba(0,0,0,0.1)",
              borderRadius: "0 0 0 8px",
              transform: "rotate(45deg)",
            },
          }}
        />
        {/* Plus Icon */}
        <IconButton
          className="sticky-plus-icon"
          onClick={handlePlusClick}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 32,
            height: 32,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            color: "#666",
            zIndex: 51,
            pointerEvents: "all",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 1)",
              color: "#333",
              transform: "translate(-50%, -50%) scale(1.1)",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            },
            "& .MuiSvgIcon-root": {
              fontSize: "18px",
            },
          }}
        >
          <AddIcon />
        </IconButton>
        {/* Hover effect layer */}
        {isHovered && (
          <Box
            sx={{
              position: "absolute",
              width: 58,
              height: 58,
              backgroundColor: childColor,
              borderRadius: 1,
              boxShadow: "0 3px 8px rgba(0,0,0,0.2)",
              top: 1,
              left: 0.5,
              opacity: 0.9,
              transform: "translateY(-4px) translateX(3px) rotate(2deg)",
              transition: "all 0.3s ease",
              zIndex: -1,
              border: "1px solid rgba(255,255,255,0.2)",
              pointerEvents: "none",
            }}
          />
        )}
      </Box>
    </>
  );
};

const GenericRetrospectiveInner: React.FC<GenericRetrospectiveProps> = ({
  sprintId,
  sprintName,
  reactFlow,
  onBack,
  templateId,
}) => {
  const {
    ReactFlow,
    addEdge,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    useReactFlow,
  } = reactFlow;

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesState] = useEdgesState([]);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [isReactFlowReady, setIsReactFlowReady] = useState(false);
  const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });
  const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null);
  const [originalChildPositions, setOriginalChildPositions] = useState<
    Map<string, { x: number; y: number }>
  >(new Map());

  // Add these new state variables for better drag tracking
  const [isDraggingColumn, setIsDraggingColumn] = useState(false);
  const [lastColumnDragPosition, setLastColumnDragPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Add hover timeout ref for debouncing
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const reactFlowInstance = useReactFlow();
  const flowRef = useRef<HTMLDivElement>(null);
  const lastViewportRef = useRef<any>(null);
  const nodeRefs = useRef<Map<string, ResizableNoteNodeRef>>(new Map());

  // ---------- GET TEMPLATE CONFIG ---------------
  const templateConfig = retrospectiveTemplates[templateId];

  if (!templateConfig) {
    throw new Error(
      `Template configuration not found for templateId: ${templateId}`
    );
  }

  const {
    templateId: actualTemplateId,
    templateName,
    columns,
  } = templateConfig;

  // ---------- SOCKET & DATA ---------------
  const {
    socket,
    connected,
    cursors,
    users,
    draggingNodes,
    activeConnections,
    editingNodes,
    emit,
    on,
    updateCursorPosition,
    updateNodeDragPosition,
    updateTextChange,
    endNodeDrag,
    sendConnectionPreview,
    endConnectionPreview,
    startNodeEditing,
    endNodeEditing,
    userId,
    userName,
    userColor,
    sendEmojiReactionAdd,
    sendEmojiReactionRemove,
    sendCommentAdd,
    sendCommentReactionAdd,
    sendCommentReactionRemove,
  } = useSocket(sprintId, sprintName);

  // ------------ SUPPORT HOOKS & LOGIC --------------
  useEffect(() => {
    if (reactFlowInstance && !isReactFlowReady) {
      const timer = setTimeout(() => {
        setIsReactFlowReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [reactFlowInstance]);

  // Function to check if two rectangles overlap
  const isOverlapping = useCallback(
    (
      rect1: { x: number; y: number; width: number; height: number },
      rect2: { x: number; y: number; width: number; height: number }
    ) => {
      const { x: x1, y: y1, width: w1, height: h1 } = rect1;
      const { x: x2, y: y2, width: w2, height: h2 } = rect2;

      // Check horizontal overlap
      const horizontalOverlap = x1 < x2 + w2 && x1 + w1 > x2;
      // Check vertical overlap
      const verticalOverlap = y1 < y2 + h2 && y1 + h1 > y2;

      return horizontalOverlap && verticalOverlap;
    },
    []
  );

  // Function to calculate z-index based on overlap and position with higher separation
  const calculateColumnZIndex = useCallback(
    (nodesList: any[]) => {
      const columnNodes = nodesList.filter((node) => node.type === "column");

      return nodesList.map((node) => {
        if (node.type === "column") {
          // Base z-index for columns
          let baseZIndex = 10;

          // Create rectangle for current column
          const currentRect = {
            x: node.position.x,
            y: node.position.y,
            width: node.data.width || 320,
            height: node.data.height || 650,
          };

          // Check overlaps with other columns
          const overlappingColumns = columnNodes.filter((otherColumn) => {
            if (otherColumn.id === node.id) return false;

            const otherRect = {
              x: otherColumn.position.x,
              y: otherColumn.position.y,
              width: otherColumn.data.width || 320,
              height: otherColumn.data.height || 650,
            };

            return isOverlapping(currentRect, otherRect);
          });

          if (overlappingColumns.length > 0) {
            // If overlapping, columns with smaller y position (higher up) get higher z-index
            const allOverlappingNodes = [...overlappingColumns, node];
            const sortedByY = allOverlappingNodes.sort(
              (a, b) => a.position.y - b.position.y
            );

            const currentIndex = sortedByY.findIndex(
              (col) => col.id === node.id
            );
            // Higher position (smaller index) gets much higher z-index with bigger separation
            baseZIndex = 1000 + (sortedByY.length - currentIndex) * 500;
          }

          return {
            ...node,
            zIndex: baseZIndex,
            style: {
              ...node.style,
              zIndex: baseZIndex,
            },
          };
        }
        return node;
      });
    },
    [isOverlapping]
  );

  // Function to ensure proper z-index for child nodes based on parent column z-index with large separation
  const ensureChildZIndex = useCallback((nodesList: any[]) => {
    const columnNodes = nodesList.filter((node) => node.type === "column");

    return nodesList.map((node) => {
      if (node.type !== "column") {
        // Find parent column
        const parentColumn = columnNodes.find(
          (col) => col.data.columnId === node.data.columnId
        );

        if (parentColumn) {
          // Child z-index should be parent z-index + 10000 to ensure it's always above with large separation
          const childZIndex = (parentColumn.zIndex || 10) + 10000;
          return {
            ...node,
            zIndex: childZIndex,
            style: {
              ...node.style,
              zIndex: childZIndex,
            },
          };
        } else {
          // Default very high z-index if no parent found
          return {
            ...node,
            zIndex: 10000,
            style: {
              ...node.style,
              zIndex: 10000,
            },
          };
        }
      }
      return node;
    });
  }, []);

  // FIXED: Function to calculate minimum required dimensions for a column based on its children
  const calculateMinimumColumnDimensions = useCallback(
    (columnId: string, nodesList: any[]) => {
      const childNodes = nodesList.filter(
        (node) => node.data?.columnId === columnId && node.type !== "column"
      );

      const nodeWidth = 120;
      const nodeHeight = 120;
      const paddingY = 15;
      const paddingX = 25;
      const headerHeight = 100;

      if (childNodes.length === 0) {
        // No children, return absolute minimum
        return {
          minWidth: 250, // Absolute minimum width
          minHeight: headerHeight + paddingY * 2 + 50,
        };
      }

      // Calculate minimum width needed to fit at least one child per row
      const minWidth = Math.max(250, nodeWidth + paddingX * 2);

      // FIXED: Calculate minimum height based on actual child positions instead of grid assumption
      const parentColumn = nodesList.find(
        (node) => node.type === "column" && node.data.columnId === columnId
      );

      if (!parentColumn) {
        return {
          minWidth,
          minHeight: columns.find((col) => col.id === columnId)?.height || 650,
        };
      }

      // Find the bottommost child relative to the column
      let maxBottomY = 0;
      childNodes.forEach((child) => {
        const relativeY = child.position.y - parentColumn.position.y;
        const bottomY = relativeY + nodeHeight;
        if (bottomY > maxBottomY) {
          maxBottomY = bottomY;
        }
      });

      // Add some bottom padding
      const calculatedMinHeight = maxBottomY + paddingY + 30;
      const originalMinHeight =
        columns.find((col) => col.id === columnId)?.height || 650;

      return {
        minWidth,
        minHeight: Math.max(calculatedMinHeight, originalMinHeight),
      };
    },
    [columns]
  );

  // FIXED: Function to calculate dynamic height for a column based on actual child node positions
  const calculateColumnHeight = useCallback(
    (columnId: string, nodesList: any[], columnWidth?: number) => {
      const childNodes = nodesList.filter(
        (node) => node.data?.columnId === columnId && node.type !== "column"
      );

      const nodeHeight = 120;
      const paddingY = 15;
      const headerHeight = 100; // Height for title and subtitle

      // If no children, return minimum height
      if (childNodes.length === 0) {
        const column = columns.find((col) => col.id === columnId);
        return column?.height || 650;
      }

      // FIXED: Find the parent column to calculate relative positions
      const parentColumn = nodesList.find(
        (node) => node.type === "column" && node.data.columnId === columnId
      );

      if (!parentColumn) {
        const column = columns.find((col) => col.id === columnId);
        return column?.height || 650;
      }

      // FIXED: Calculate height based on actual child positions, not grid layout
      let maxBottomY = 0;

      childNodes.forEach((child) => {
        // Calculate relative position to the column
        const relativeY = child.position.y - parentColumn.position.y;
        const bottomY = relativeY + nodeHeight;

        if (bottomY > maxBottomY) {
          maxBottomY = bottomY;
        }
      });

      // Add some bottom padding
      const totalHeight = maxBottomY + paddingY + 50;

      // Ensure it's not smaller than the original minimum height
      const column = columns.find((col) => col.id === columnId);
      return Math.max(totalHeight, column?.height || 650);
    },
    [columns]
  );

  // Function to update column heights based on child nodes
  const updateColumnHeights = useCallback(
    (nodesList: any[]) => {
      return nodesList.map((node) => {
        if (node.type === "column") {
          const newHeight = calculateColumnHeight(
            node.data.columnId,
            nodesList,
            node.data.width
          );
          return {
            ...node,
            data: {
              ...node.data,
              height: newHeight,
            },
            style: {
              ...node.style,
              height: newHeight,
            },
          };
        }
        return node;
      });
    },
    [calculateColumnHeight]
  );

  // Function to update child positions only for newly added nodes or when explicitly needed (like resize)
  const updateChildPositions = useCallback(
    (nodesList: any[], forceReorganize = false) => {
      const columnNodes = nodesList.filter((node) => node.type === "column");
      const childNodes = nodesList.filter((node) => node.type !== "column");

      const updatedNodes = nodesList.map((node) => {
        if (node.type !== "column") {
          // Find parent column
          const parentColumn = columnNodes.find(
            (col) => col.data.columnId === node.data.columnId
          );

          if (parentColumn && forceReorganize) {
            // Only reorganize if forced (like on resize or add new node)
            // Calculate relative position within parent
            const nodeWidth = 120;
            const nodeHeight = 120;
            const horizontalSpacing = 30;
            const verticalSpacing = 25;
            const paddingX = 25;
            const paddingY = 15;

            // Calculate nodes per row based on parent column width
            const availableWidth =
              (parentColumn.data.width || 320) - paddingX * 2;
            const nodesPerRow = Math.max(
              1,
              Math.floor(
                (availableWidth + horizontalSpacing) /
                  (nodeWidth + horizontalSpacing)
              )
            );

            // Get all siblings in same column, sorted by their current index or creation time
            const siblings = childNodes
              .filter((n) => n.data.columnId === node.data.columnId)
              .sort((a, b) => {
                // Try to maintain existing order based on position
                const aY = a.position.y - parentColumn.position.y;
                const bY = b.position.y - parentColumn.position.y;
                if (Math.abs(aY - bY) > 50) {
                  // Different rows
                  return aY - bY;
                }
                // Same row, sort by x
                const aX = a.position.x - parentColumn.position.x;
                const bX = b.position.x - parentColumn.position.x;
                return aX - bX;
              });

            const nodeIndex = siblings.findIndex((n) => n.id === node.id);

            if (nodeIndex !== -1) {
              const row = Math.floor(nodeIndex / nodesPerRow);
              const col = nodeIndex % nodesPerRow;

              const xOffset = paddingX + col * (nodeWidth + horizontalSpacing);
              const yOffset =
                100 + paddingY + row * (nodeHeight + verticalSpacing);
              const xPosition = parentColumn.position.x + xOffset;
              const yPosition = parentColumn.position.y + yOffset;

              return {
                ...node,
                position: {
                  x: xPosition,
                  y: yPosition,
                },
              };
            }
          } else if (parentColumn && !forceReorganize) {
            // FIXED: Ensure child nodes stay within parent bounds even without reorganization
            const nodeWidth = 120;
            const nodeHeight = 120;
            const paddingX = 25;
            const paddingY = 15;
            const headerHeight = 100;

            // Calculate boundaries
            const minX = parentColumn.position.x + paddingX;
            const maxX =
              parentColumn.position.x +
              (parentColumn.data.width || 320) -
              nodeWidth -
              paddingX;
            const minY = parentColumn.position.y + headerHeight + paddingY;
            const maxY =
              parentColumn.position.y +
              (parentColumn.data.height || 650) -
              nodeHeight -
              paddingY;

            // Check if current position is outside bounds
            let newX = node.position.x;
            let newY = node.position.y;

            // Constrain X position
            if (newX < minX) newX = minX;
            if (newX > maxX) newX = maxX;

            // Constrain Y position
            if (newY < minY) newY = minY;
            if (newY > maxY) newY = maxY;

            // Only update position if it was constrained
            if (newX !== node.position.x || newY !== node.position.y) {
              return {
                ...node,
                position: {
                  x: newX,
                  y: newY,
                },
              };
            }
          }
        }
        return node;
      });

      // Apply column z-index calculations first, then child z-index
      const withColumnZIndex = calculateColumnZIndex(updatedNodes);
      return ensureChildZIndex(withColumnZIndex);
    },
    [calculateColumnZIndex, ensureChildZIndex]
  );

  // Function to create default nodes for each column
  const createDefaultNodes = useCallback(() => {
    const defaultNodes: any[] = [];

    columns.forEach((column) => {
      defaultNodes.push({
        id: `column-${column.id}`,
        type: "column",
        position: {
          x: column.x,
          y: column.y,
        },
        data: {
          title: column.title,
          subtitle: column.subtitle,
          color: column.color,
          childColor: column.childColor,
          width: column.width,
          height: column.height,
          columnId: column.id,
          showStickyPad: column.showStickyPad,
        },
        style: {
          width: column.width,
          height: column.height,
          zIndex: 10,
        },
        draggable: true,
        selectable: true,
        zIndex: 10,
      });

      // Only add default child notes if placeholderText exists
      if (column.placeholderText) {
        const nodeWidth = 120;
        const nodeHeight = 120;
        const horizontalSpacing = 30;
        const verticalSpacing = 25;
        const paddingX = 25;
        const paddingY = 15;

        for (let i = 0; i < 4; i++) {
          const nodeId = `${column.id}-child-${i + 1}`;
          const row = Math.floor(i / 2);
          const col = i % 2;
          const xOffset = paddingX + col * (nodeWidth + horizontalSpacing);
          const yOffset = 100 + paddingY + row * (nodeHeight + verticalSpacing);
          const xPosition = column.x + xOffset;
          const yPosition = column.y + yOffset;

          defaultNodes.push({
            id: nodeId,
            type: "resizableNote",
            position: {
              x: xPosition,
              y: yPosition,
            },
            data: {
              text: column.placeholderText,
              width: nodeWidth,
              height: nodeHeight,
              color: column.childColor,
              shape: "rectangle",
              userId: "",
              reactions: [],
              columnId: column.id,
              isChild: true,
            } as NoteNodeData,
            style: {
              width: nodeWidth,
              height: nodeHeight,
              zIndex: 10000,
            },
            draggable: true,
            zIndex: 10000,
          });
        }
      }
    });

    const withColumnHeights = updateColumnHeights(defaultNodes);
    const withColumnZIndex = calculateColumnZIndex(withColumnHeights);
    return ensureChildZIndex(withColumnZIndex);
  }, [columns, updateColumnHeights, calculateColumnZIndex, ensureChildZIndex]);

  const prepareTemplateData = useCallback(() => {
    const canvasWidth = 1500;
    const canvasHeight = 800;

    const flowNodes = nodes.map((node) => ({
      id: node.id,
      type: node.type,
      position: node.position,
      data: {
        ...node.data,
        reactions: node.data.reactions || [],
        comments: node.data.comments || [],
      },
      style: node.style || {},
      draggable: node.draggable !== false,
      selectable: node.selectable !== false,
      zIndex: node.zIndex,
      timestamp: Date.now(),
    }));

    const flowEdges = edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      type: edge.type || "deleteable",
      style: edge.style || {
        stroke: "#374151",
        strokeWidth: 2,
      },
      markerEnd: edge.markerEnd || {
        type: "arrowclosed",
        color: "#374151",
      },
      zIndex: edge.zIndex || 999,
    }));

    const statistics = {
      totalNodes: nodes.filter((n) => n.type !== "column").length,
      totalEdges: edges.length,
      totalReactions: nodes.reduce(
        (total, node) => total + (node.data.reactions?.length || 0),
        0
      ),
      totalComments: nodes.reduce(
        (total, node) => total + (node.data.comments?.length || 0),
        0
      ),
      totalCommentReactions: nodes.reduce(
        (total, node) =>
          total +
          (node.data.comments || []).reduce(
            (commentTotal, comment) =>
              commentTotal + (comment.reactions?.length || 0),
            0
          ),
        0
      ),
      userCount: new Set([
        ...nodes.map((node) => node.data.userId || userId).filter(Boolean),
        ...nodes
          .flatMap((node) => node.data.reactions || [])
          .map((reaction) => reaction.userId)
          .filter(Boolean),
        ...nodes
          .flatMap((node) => node.data.comments || [])
          .map((comment) => comment.userId)
          .filter(Boolean),
      ]).size,
      columnStats: columns.map((column) => ({
        id: column.id,
        title: column.title,
        nodeCount: nodes.filter(
          (node) => node.data.columnId === column.id && node.type !== "column"
        ).length,
        reactionCount: nodes
          .filter(
            (node) => node.data.columnId === column.id && node.type !== "column"
          )
          .reduce(
            (total, node) => total + (node.data.reactions?.length || 0),
            0
          ),
        commentCount: nodes
          .filter(
            (node) => node.data.columnId === column.id && node.type !== "column"
          )
          .reduce(
            (total, node) => total + (node.data.comments?.length || 0),
            0
          ),
      })),
    };

    return [
      {
        flowData: {
          nodes: flowNodes,
          edges: flowEdges,
        },
        canvasMetadata: {
          width: canvasWidth,
          height: canvasHeight,
          // viewport: reactFlowInstance.getViewport(),
        },
        columns: columns,
        statistics,
        lastUpdated: new Date().toISOString(),
      },
    ];
  }, [nodes, edges, reactFlowInstance, userId, columns]);

  // Format loaded nodes for retrospective
  const formatLoadedNodes = useCallback(
    (loadedNodes: any[]) => {
      const formattedNodes = loadedNodes.map((node: any) => ({
        id: node.id,
        type: node.type || "resizableNote",
        position: node.position || { x: 0, y: 0 },
        data: {
          ...node.data,
          reactions: node.data.reactions || [],
          comments: node.data.comments || [],
        },
        style: {
          ...node.style,
          zIndex: node.type === "column" ? 10 : 10000,
        },
        draggable:
          node.type === "column"
            ? true
            : node.draggable !== undefined
            ? node.draggable
            : true,
        selectable:
          node.type === "column"
            ? true
            : node.selectable !== undefined
            ? node.selectable
            : true,
        zIndex: node.type === "column" ? 10 : 10000,
      }));

      const withColumnHeights = updateColumnHeights(formattedNodes);
      const withColumnZIndex = calculateColumnZIndex(withColumnHeights);
      return ensureChildZIndex(withColumnZIndex);
    },
    [updateColumnHeights, calculateColumnZIndex, ensureChildZIndex]
  );

  // Format loaded edges for retrospective
  const formatLoadedEdges = useCallback((loadedEdges: any[]) => {
    return loadedEdges.map((edge: any) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      type: edge.type || "deleteable",
      style: edge.style || {
        stroke: "#374151",
        strokeWidth: 2,
      },
      markerEnd: edge.markerEnd || {
        type: "arrowclosed",
        color: "#374151",
      },
      zIndex: edge.zIndex || 999,
    }));
  }, []);

  // Use common hooks
  const {
    isDataLoaded,
    setIsDataLoaded,
    lastSaveTime,
    saveStatus,
    hasPendingSave,
    markUserChange,
    saveFlowData,
    loadExistingData,
    isLoadingSprintDetails,
    isUpdatingSprint,
  } = useRetrospectiveData(
    sprintId,
    actualTemplateId,
    templateName,
    nodes,
    edges,
    reactFlowInstance,
    userId,
    prepareTemplateData
  );

  const {
    handleNodeDelete,
    handleEdgeDelete,
    handleNodeColorChange,
    handleNodeTextChange,
    handleNodeTextChangeRealtime,
    handleNodeEditStart,
    handleNodeEditEnd,
    handleNodeEmojiReactionAdd,
    handleNodeEmojiReactionToggle,
    handleAddComment,
    handleAddCommentReaction,
    handleRemoveCommentReaction,
    handleNodesChange: baseHandleNodesChange,
    handleEdgesChange,
  } = useRetrospectiveHandlers(
    nodes,
    setNodes,
    setEdges,
    edges,
    emit,
    userId,
    userName,
    userColor,
    users,
    markUserChange,
    sendEmojiReactionAdd,
    sendEmojiReactionRemove,
    sendCommentAdd,
    sendCommentReactionAdd,
    sendCommentReactionRemove,
    updateTextChange,
    startNodeEditing,
    endNodeEditing,
    updateNodeDragPosition,
    endNodeDrag,
    onNodesChange,
    onEdgesState
  );

  const { getCursorsData, getDraggingPreviewsData } = useRetrospectiveRendering(
    cursors,
    draggingNodes,
    activeConnections,
    nodes,
    users,
    viewport,
    reactFlowInstance
  );

  // Socket listeners
  useRetrospectiveSocketListeners(
    connected,
    on,
    setNodes,
    setEdges,
    reactFlowInstance,
    userId
  );

  // Add node to a specific column with grid positioning inside the parent column
  const addNodeToColumn = useCallback(
    (columnId: string) => {
      const column = columns.find((col) => col.id === columnId);
      const parentColumn = nodes.find(
        (node) => node.type === "column" && node.data.columnId === columnId
      );

      if (!column || !parentColumn) return;

      const columnNodes = nodes.filter(
        (node) => node.data.columnId === columnId && node.type !== "column"
      );

      const nodeWidth = 120;
      const nodeHeight = 120;
      const horizontalSpacing = 30;
      const verticalSpacing = 25;
      const paddingX = 25;
      const paddingY = 15;

      // Calculate nodes per row based on current column width
      const availableWidth = (parentColumn.data.width || 320) - paddingX * 2;
      const nodesPerRow = Math.max(
        1,
        Math.floor(
          (availableWidth + horizontalSpacing) / (nodeWidth + horizontalSpacing)
        )
      );

      const row = Math.floor(columnNodes.length / nodesPerRow);
      const col = columnNodes.length % nodesPerRow;

      const xOffset = paddingX + col * (nodeWidth + horizontalSpacing);
      const yOffset = 100 + paddingY + row * (nodeHeight + verticalSpacing);
      const xPosition = parentColumn.position.x + xOffset;
      const yPosition = parentColumn.position.y + yOffset;

      const newNode = {
        id: `${columnId}-child-${Date.now()}-${userId}`,
        type: "resizableNote",
        position: {
          x: xPosition,
          y: yPosition,
        },
        data: {
          text: column.placeholderText || "New note...",
          width: nodeWidth,
          height: nodeHeight,
          color: column.childColor,
          shape: "rectangle",
          userId: userId,
          reactions: [],
          columnId: columnId,
          isChild: true,
        } as NoteNodeData,
        style: {
          width: nodeWidth,
          height: nodeHeight,
          zIndex: 10000,
        },
        draggable: true,
        zIndex: 10000,
      };

      setNodes((nds) => {
        const updatedNodes = [...nds, newNode];
        // Only update column heights for newly added nodes
        const withColumnHeights = updateColumnHeights(updatedNodes);
        const withColumnZIndex = calculateColumnZIndex(withColumnHeights);
        return ensureChildZIndex(withColumnZIndex);
      });

      markUserChange();
      emit("NODE_ADDED", {
        node: newNode,
        addedBy: userId,
      });
    },
    [
      setNodes,
      emit,
      userId,
      markUserChange,
      nodes,
      columns,
      updateColumnHeights,
      calculateColumnZIndex,
      ensureChildZIndex,
    ]
  );

  // FIXED: Custom Column Node Component with enhanced resize capability that enforces minimum dimensions
  const ColumnNode = useCallback(
    ({ data, id, selected }: any) => {
      const { NodeResizer } = reactFlow;

      const handleColumnResize = useCallback(
        (event: any, params: { width: number; height: number }) => {
          const { width, height } = params;

          setNodes((nds) => {
            // FIXED: Calculate minimum required dimensions based on current children
            const { minWidth, minHeight } = calculateMinimumColumnDimensions(
              data.columnId,
              nds
            );

            // FIXED: Enforce minimum dimensions
            const constrainedWidth = Math.max(width, minWidth);
            const constrainedHeight = Math.max(height, minHeight);

            // Update column dimensions with constrained values
            const updatedNodes = nds.map((node) =>
              node.id === id
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      width: constrainedWidth,
                      height: constrainedHeight,
                    },
                    style: {
                      ...node.style,
                      width: constrainedWidth,
                      height: constrainedHeight,
                    },
                  }
                : node
            );

            // FIXED: Force reorganization and constraint of child positions after column resize
            const withChildPositions = updateChildPositions(updatedNodes, true);
            const withColumnZIndex = calculateColumnZIndex(withChildPositions);
            return ensureChildZIndex(withColumnZIndex);
          });

          markUserChange();
        },
        [id, data.columnId]
      );

      // FIXED: Calculate dynamic minimum dimensions based on current children
      const currentChildren = nodes.filter(
        (node) =>
          node.data?.columnId === data.columnId && node.type !== "column"
      );
      const { minWidth, minHeight } = calculateMinimumColumnDimensions(
        data.columnId,
        nodes
      );

      return (
        <Box
          sx={{
            width: data.width,
            height: data.height,
            backgroundColor: data.color,
            borderRadius: 3,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            outline: selected ? "3px solid #1976d2" : "none",
            outlineOffset: selected ? "2px" : "0",
          }}
        >
          <NodeResizer
            color="#1976d2"
            isVisible={selected}
            minWidth={minWidth}
            minHeight={minHeight}
            onResize={handleColumnResize}
          />

          <Paper
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              borderRadius: "12px 12px 0 0",
              p: 2,
              textAlign: "center",
              minHeight: 80,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                mb: 1,
                color: "#000",
              }}
            >
              {data.title}
            </Typography>
            {data.subtitle && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "10px",
                  color: "#666",
                  mb: 1,
                }}
              >
                {data.subtitle}
              </Typography>
            )}
          </Paper>
          <Box
            sx={{
              flex: 1,
              position: "relative",
              padding: 1,
            }}
          />
          {/* Conditionally render StickyNotePad based on showStickyPad */}
          {data.showStickyPad && (
            <StickyNotePad
              columnId={data.columnId}
              color={data.color}
              childColor={data.childColor}
              onCreateNoteFromClick={addNodeToColumn}
              position={{ x: 6, y: 15 }}
              reactFlowInstance={reactFlowInstance}
              flowRef={flowRef}
            />
          )}
        </Box>
      );
    },
    [
      addNodeToColumn,
      reactFlow,
      setNodes,
      markUserChange,
      updateChildPositions,
      calculateColumnZIndex,
      ensureChildZIndex,
      calculateMinimumColumnDimensions,
      nodes,
      reactFlowInstance,
      flowRef,
    ]
  );

  const getClosestColumn = useCallback(
    (position: { x: number; y: number }) => {
      let closestColumn = null;
      let minDistance = Infinity;

      const columnNodes = nodes.filter((node) => node.type === "column");

      columnNodes.forEach((columnNode) => {
        const columnCenterX =
          columnNode.position.x + (columnNode.data.width || 320) / 2;
        const columnCenterY =
          columnNode.position.y + (columnNode.data.height || 650) / 2;
        const distance = Math.sqrt(
          Math.pow(position.x - columnCenterX, 2) +
            Math.pow(position.y - columnCenterY, 2)
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestColumn = columns.find(
            (col) => col.id === columnNode.data.columnId
          );
        }
      });

      return closestColumn;
    },
    [columns, nodes]
  );

  // NEW: Handle node hover with debouncing
  const handleNodeMouseEnter = useCallback((nodeId: string) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Set a small delay before showing the toolbar to prevent flickering
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredNodeId(nodeId);
    }, 150);
  }, []);

  const handleNodeMouseLeave = useCallback(() => {
    // Clear the timeout if user leaves before delay completes
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Hide the toolbar with a small delay to allow moving to the toolbar itself
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredNodeId(null);
    }, 100);
  }, []);

  // Handle toolbar hover to keep it visible
  const handleToolbarMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, []);

  const handleToolbarMouseLeave = useCallback(() => {
    setHoveredNodeId(null);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // FIXED: Enhanced nodes change handler with proper child constraint handling
  const handleNodesChange = useCallback(
    (changes: any[]) => {
      baseHandleNodesChange(changes, (processedChanges: any[]) => {
        let shouldUpdateColumnHeights = false;
        let shouldUpdateChildPositions = false;
        let hasColumnDragChanges = false;
        let shouldRecalculateZIndex = false;

        processedChanges.forEach((change) => {
          const node = nodes.find((n) => n.id === change.id);

          // Skip processing for column selection changes to prevent unwanted repositioning
          if (change.type === "select" && node?.type === "column") {
            // Recalculate z-index when column is selected (in case of overlaps)
            shouldRecalculateZIndex = true;
            return;
          }

          // Handle column drag start
          if (
            change.type === "position" &&
            change.dragging &&
            node?.type === "column"
          ) {
            hasColumnDragChanges = true;
            shouldRecalculateZIndex = true;

            if (!draggingColumnId) {
              setDraggingColumnId(change.id);
              setIsDraggingColumn(true);
              setLastColumnDragPosition(change.position);

              // Store original child positions relative to column
              const childNodes = nodes.filter(
                (n) =>
                  n.data?.columnId === node.data.columnId && n.type !== "column"
              );
              const originalPositions = new Map();
              childNodes.forEach((childNode) => {
                originalPositions.set(childNode.id, {
                  x: childNode.position.x - node.position.x,
                  y: childNode.position.y - node.position.y,
                });
              });
              setOriginalChildPositions(originalPositions);
            }
          }

          // Handle column dragging - update children in real-time with z-index
          if (
            change.type === "position" &&
            change.dragging &&
            change.id === draggingColumnId &&
            node?.type === "column"
          ) {
            hasColumnDragChanges = true;
            shouldRecalculateZIndex = true;
            setLastColumnDragPosition(change.position);

            // Update child positions in real-time during drag with proper z-index
            setNodes((nds) => {
              const updatedNodes = nds.map((n) => {
                if (
                  n.data?.columnId === node?.data.columnId &&
                  n.type !== "column"
                ) {
                  const originalPos = originalChildPositions.get(n.id);
                  if (originalPos) {
                    return {
                      ...n,
                      position: {
                        x: change.position.x + originalPos.x,
                        y: change.position.y + originalPos.y,
                      },
                    };
                  }
                }
                return n;
              });

              // Recalculate z-index during drag
              const withColumnZIndex = calculateColumnZIndex(updatedNodes);
              return ensureChildZIndex(withColumnZIndex);
            });
          }

          // Handle column drag end
          if (
            change.type === "position" &&
            !change.dragging &&
            change.id === draggingColumnId &&
            node?.type === "column"
          ) {
            hasColumnDragChanges = true;
            shouldRecalculateZIndex = true;
            setDraggingColumnId(null);
            setIsDraggingColumn(false);
            setLastColumnDragPosition(null);
            setOriginalChildPositions(new Map());

            // Don't reorganize after column drag ends
            shouldRecalculateZIndex = true;
          }

          // FIXED: Handle child node movement with proper boundary constraints
          if (
            change.type === "position" &&
            change.position &&
            !change.dragging &&
            !isDraggingColumn &&
            node?.data?.isChild
          ) {
            const closestColumn = getClosestColumn(change.position);
            if (closestColumn && node.data.columnId !== closestColumn.id) {
              // Store the original color before changing columns
              const originalColor = node.data.color;

              setNodes((nds) => {
                const updatedNodes = nds.map((n) =>
                  n.id === change.id
                    ? {
                        ...n,
                        data: {
                          ...n.data,
                          columnId: closestColumn.id,
                          // Keep the original color instead of changing to destination column color
                          color: originalColor,
                        },
                      }
                    : n
                );
                const withColumnHeights = updateColumnHeights(updatedNodes);
                // Force reorganization when moving between columns
                const withChildPositions = updateChildPositions(
                  withColumnHeights,
                  true
                );
                const withColumnZIndex =
                  calculateColumnZIndex(withChildPositions);
                return ensureChildZIndex(withColumnZIndex);
              });
              shouldUpdateColumnHeights = false;
              shouldUpdateChildPositions = false;
            } else {
              // FIXED: Constrain child within parent bounds when staying in same column
              shouldUpdateColumnHeights = true;
              shouldUpdateChildPositions = true; // FIXED: Enable constraint checking
            }
          }

          // Check if it's a remove change (node deletion)
          if (change.type === "remove" && !isDraggingColumn) {
            shouldUpdateColumnHeights = true;
            shouldRecalculateZIndex = true;
          }
        });

        // FIXED: Update child positions to ensure they stay within bounds
        if (
          !isDraggingColumn &&
          !hasColumnDragChanges &&
          shouldUpdateChildPositions
        ) {
          setNodes((nds) => {
            const withColumnHeights = updateColumnHeights(nds);
            // FIXED: Apply child position constraints
            const withChildPositions = updateChildPositions(
              withColumnHeights,
              false
            );
            const withColumnZIndex = calculateColumnZIndex(withChildPositions);
            return ensureChildZIndex(withColumnZIndex);
          });
        } else if (
          !isDraggingColumn &&
          !hasColumnDragChanges &&
          shouldUpdateColumnHeights
        ) {
          // Update column heights if needed (but don't reorganize child positions)
          setNodes((nds) => {
            const withColumnHeights = updateColumnHeights(nds);
            const withColumnZIndex = calculateColumnZIndex(withColumnHeights);
            return ensureChildZIndex(withColumnZIndex);
          });
        }

        // Recalculate z-index if needed (for selection, overlaps, etc.)
        if (shouldRecalculateZIndex && !hasColumnDragChanges) {
          setNodes((nds) => {
            const withColumnZIndex = calculateColumnZIndex(nds);
            return ensureChildZIndex(withColumnZIndex);
          });
        }
      });
    },
    [
      baseHandleNodesChange,
      nodes,
      draggingColumnId,
      originalChildPositions,
      isDraggingColumn,
      getClosestColumn,
      setNodes,
      updateColumnHeights,
      updateChildPositions,
      calculateColumnZIndex,
      ensureChildZIndex,
    ]
  );

  // Save data when user makes changes
  useEffect(() => {
    if (isDataLoaded && hasPendingSave && nodes.length > 0) {
      saveFlowData();
    }
  }, [nodes, edges, isDataLoaded, hasPendingSave, saveFlowData]);

  // Load existing data when sprint details and ReactFlow are both ready
  useEffect(() => {
    if (!isLoadingSprintDetails && !isDataLoaded && isReactFlowReady) {
      const timer = setTimeout(() => {
        loadExistingData(
          setNodes,
          setEdges,
          createDefaultNodes,
          formatLoadedNodes,
          formatLoadedEdges
        );
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [
    isLoadingSprintDetails,
    isDataLoaded,
    isReactFlowReady,
    loadExistingData,
    setNodes,
    setEdges,
    createDefaultNodes,
    formatLoadedNodes,
    formatLoadedEdges,
  ]);

  // FIXED: Handle node deletion with column height update and z-index recalculation (no reorganization)
  const customHandleNodeDelete = useCallback(
    (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (node && node.type === "column") {
        return;
      }

      setNodes((nds) => {
        const filteredNodes = nds.filter((n) => n.id !== nodeId);
        const withColumnHeights = updateColumnHeights(filteredNodes);
        // FIXED: Skip reorganization on delete to preserve manual positioning
        const withColumnZIndex = calculateColumnZIndex(withColumnHeights);
        return ensureChildZIndex(withColumnZIndex);
      });

      handleNodeDelete(nodeId);

      // Hide toolbar if the deleted node was being hovered
      if (hoveredNodeId === nodeId) {
        setHoveredNodeId(null);
      }
    },
    [
      handleNodeDelete,
      nodes,
      setNodes,
      updateColumnHeights,
      calculateColumnZIndex,
      ensureChildZIndex,
      hoveredNodeId,
    ]
  );

  // Handle cursor movement
  const handleNodeCursorMove = useCallback(
    (event: React.MouseEvent) => {
      if (flowRef.current && reactFlowInstance) {
        const rect = flowRef.current.getBoundingClientRect();
        const viewport = reactFlowInstance.getViewport();
        const x = (event.clientX - rect.left - viewport.x) / viewport.zoom;
        const y = (event.clientY - rect.top - viewport.y) / viewport.zoom;
        updateCursorPosition({ x, y });
      }
    },
    [reactFlowInstance, updateCursorPosition]
  );

  // Define custom node types
  const nodeTypes: any = useMemo(
    () => ({
      column: ColumnNode,
      resizableNote: (props: any) => {
        const editingState = editingNodes.get(props.id);
        const isBeingEditedByOther =
          editingState && editingState.userId !== userId;

        return (
          <ResizableNoteNode
            {...props}
            ref={(nodeRef: any) => {
              if (nodeRef) {
                nodeRefs.current.set(props.id, nodeRef);
              } else {
                nodeRefs.current.delete(props.id);
              }
            }}
            onCursorMove={handleNodeCursorMove}
            onTextChange={handleNodeTextChange}
            onTextChangeRealtime={handleNodeTextChangeRealtime}
            onEditStart={handleNodeEditStart}
            onEditEnd={handleNodeEditEnd}
            onEmojiToggle={handleNodeEmojiReactionToggle}
            isBeingEditedByOther={isBeingEditedByOther}
            editingUser={editingState}
            currentUserId={userId}
            isSelected={hoveredNodeId === props.id}
            reactFlow={reactFlow}
            onAddComment={handleAddComment}
            onAddCommentReaction={handleAddCommentReaction}
            onRemoveCommentReaction={handleRemoveCommentReaction}
            currentUserName={userName}
            currentUserColor={userColor}
            onMouseEnter={() => handleNodeMouseEnter(props.id)}
            onMouseLeave={handleNodeMouseLeave}
          />
        );
      },
    }),
    [
      ColumnNode,
      editingNodes,
      userId,
      hoveredNodeId,
      userName,
      userColor,
      handleNodeCursorMove,
      handleNodeTextChange,
      handleNodeTextChangeRealtime,
      handleNodeEditStart,
      handleNodeEditEnd,
      handleNodeEmojiReactionToggle,
      handleAddComment,
      handleAddCommentReaction,
      handleRemoveCommentReaction,
      handleNodeMouseEnter,
      handleNodeMouseLeave,
    ]
  );

  // Define custom edge types
  const edgeTypes: any = useMemo(
    () => ({
      deleteable: (props: any) => (
        <DeleteableEdge
          {...props}
          onDelete={handleEdgeDelete}
          reactFlow={reactFlow}
        />
      ),
    }),
    [handleEdgeDelete, reactFlow]
  );

  // Handle pane click
  const handlePaneClick = useCallback(() => {
    nodeRefs.current.forEach((nodeRef, nodeId) => {
      if (nodeRef && nodeRef.isEditing) {
        nodeRef.handleSave();
      }
    });
    setHoveredNodeId(null);
  }, []);

  // Handle mouse movement
  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (flowRef.current && reactFlowInstance) {
        const rect = flowRef.current.getBoundingClientRect();
        const viewport = reactFlowInstance.getViewport();
        const x = (event.clientX - rect.left - viewport.x) / viewport.zoom;
        const y = (event.clientY - rect.top - viewport.y) / viewport.zoom;
        updateCursorPosition({ x, y });
      }
    },
    [reactFlowInstance, updateCursorPosition]
  );

  // Handle edge connections (only allow connections between child nodes)
  const onConnect: any = useCallback(
    (params: any) => {
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);

      if (!sourceNode?.data?.isChild || !targetNode?.data?.isChild) {
        return;
      }

      const newEdge = {
        id: `edge-${Date.now()}`,
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: "deleteable",
        style: {
          stroke: "#374151",
          strokeWidth: 2,
        },
        markerEnd: {
          type: "arrowclosed",
          color: "#374151",
        },
        zIndex: 999,
      };

      setEdges((eds) => addEdge(newEdge, eds));
      markUserChange();

      emit("EDGE_ADDED", {
        edge: newEdge,
        addedBy: userId,
      });
    },
    [setEdges, emit, userId, markUserChange, nodes, addEdge]
  );

  // Handle viewport changes
  const handleViewportChange = useCallback(
    (newViewport: any) => {
      setViewport(newViewport);
      if (lastViewportRef.current) {
        clearTimeout(lastViewportRef.current);
      }
      lastViewportRef.current = setTimeout(() => {
        emit("VIEWPORT_CHANGED", {
          viewport: newViewport,
          changedBy: userId,
        });
      }, 100);
    },
    [emit, userId]
  );

  // Track viewport changes
  useEffect(() => {
    if (!reactFlowInstance || !isReactFlowReady) return;

    const interval = setInterval(() => {
      const currentViewport = reactFlowInstance.getViewport();
      setViewport(currentViewport);
    }, 50);

    return () => clearInterval(interval);
  }, [reactFlowInstance, isReactFlowReady]);

  // Reset function with column height updates and z-index
  const resetRetrospective = useCallback(() => {
    const defaultNodes = createDefaultNodes();
    setNodes(defaultNodes);
    setEdges([]);

    if (reactFlowInstance) {
      reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 });

      setTimeout(() => {
        reactFlowInstance.fitView({
          padding: 0.2,
          duration: 800,
          includeHiddenNodes: false,
        });
      }, 100);
    }

    setHoveredNodeId(null);
    markUserChange();

    emit("RETROSPECTIVE_RESET", {
      resetBy: userId,
      timestamp: Date.now(),
    });
  }, [
    createDefaultNodes,
    setNodes,
    setEdges,
    reactFlowInstance,
    markUserChange,
    emit,
    userId,
  ]);

  // Don't render until ReactFlow is ready and data is loaded
  if (!isReactFlowReady || !isDataLoaded) {
    return (
      <>
        <RetrospectiveHeader
          sprintName={sprintName}
          sprintId={sprintId}
          templateName={templateName}
          connected={false}
          users={users}
          saveStatus="idle"
          isLoading={true}
          lastSaveTime={null}
          onBack={onBack}
          onChangeTemplate={() => {
            console.log("Change template clicked");
          }}
        />
        <Box
          sx={{
            height: "82vh",
            width: "100%",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography>Loading retrospective...</Typography>
        </Box>
      </>
    );
  }

  // Render functions
  const renderCursors = () => {
    const cursorsData = getCursorsData();
    return cursorsData.map((cursor) => (
      <LiveCursor
        key={cursor.key}
        x={cursor.x}
        y={cursor.y}
        userName={cursor.userName}
        color={cursor.color}
      />
    ));
  };

  const renderDraggingPreviews = () => {
    const draggingData = getDraggingPreviewsData();
    return draggingData.map((dragging) => (
      <DraggingNodePreview
        key={dragging.key}
        x={dragging.x}
        y={dragging.y}
        width={dragging.width}
        height={dragging.height}
        color={dragging.color}
        userName={dragging.userName}
        shape={dragging.shape}
      />
    ));
  };

  return (
    <>
      <RetrospectiveHeader
        sprintName={sprintName}
        sprintId={sprintId}
        templateName={templateName}
        connected={connected}
        users={users}
        saveStatus={saveStatus}
        isLoading={isLoadingSprintDetails || isUpdatingSprint}
        lastSaveTime={lastSaveTime}
        onBack={onBack}
        onChangeTemplate={() => {
          console.log("Change template clicked");
        }}
        onReset={resetRetrospective}
      />
      <Box
        sx={{
          height: "84vh",
          width: "100%",
          position: "relative",
        }}
      >
        <div ref={flowRef} style={{ width: "100%", height: "100%" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onMove={handleViewportChange}
            onMouseMove={handleMouseMove}
            onPaneClick={handlePaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView={true}
            style={{ backgroundColor: "#f8f9fa" }}
            defaultEdgeOptions={{
              type: "deleteable",
              style: {
                stroke: "#374151",
                strokeWidth: 2,
              },
              markerEnd: {
                type: "arrowclosed",
                color: "#374151",
              },
              zIndex: 999,
            }}
            proOptions={{ hideAttribution: true }}
            edgesUpdatable={true}
            edgesFocusable={true}
            elementsSelectable={true}
          >
            <Background color="#ccc" gap={20} size={1} variant="dots" />
            <Controls
              showZoom={true}
              showFitView={true}
              showInteractive={true}
            />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === "column")
                  return node.data?.color || "#f0f0f0";
                return node.data?.color || "#fff59d";
              }}
              nodeStrokeWidth={2}
              maskColor="rgba(240, 240, 240, 0.6)"
            />
          </ReactFlow>

          {renderCursors()}
          {renderDraggingPreviews()}

          {hoveredNodeId &&
            nodes.find((n) => n.id === hoveredNodeId)?.type !== "column" && (
              <div
                onMouseEnter={handleToolbarMouseEnter}
                onMouseLeave={handleToolbarMouseLeave}
              >
                <NodeToolbar
                  node={nodes.find((n) => n.id === hoveredNodeId)!}
                  onColorChange={handleNodeColorChange}
                  onEmojiAdd={handleNodeEmojiReactionAdd}
                  onEmojiToggle={handleNodeEmojiReactionToggle}
                  onDelete={customHandleNodeDelete}
                  onClose={() => setHoveredNodeId(null)}
                  viewport={viewport}
                  currentUserId={userId}
                  onAddComment={handleAddComment}
                  onAddCommentReaction={handleAddCommentReaction}
                  onRemoveCommentReaction={handleRemoveCommentReaction}
                  currentUserName={userName}
                  currentUserColor={userColor}
                />
              </div>
            )}
        </div>
      </Box>
    </>
  );
};

const GenericRetrospective: React.FC<GenericRetrospectiveProps> = (props) => {
  const { ReactFlowProvider } = props.reactFlow;

  return (
    <ReactFlowProvider>
      <GenericRetrospectiveInner {...props} />
    </ReactFlowProvider>
  );
};

export default GenericRetrospective;
